/** Звуки игры.
 *
 * Тоны синтезируются через Web Audio, а не грузятся файлами. Причины две:
 * Mini App должен открываться мгновенно, а звуковые файлы — это лишние
 * сотни килобайт и лишние сетевые запросы; и лицензионно чистый набор
 * коротких звуков всё равно пришлось бы откуда-то взять.
 *
 * Звук короткий и тихий намеренно: в судоку играют в транспорте, и громкий
 * отклик на каждую цифру выключат после третьего хода вместе со звуком
 * вообще.
 */

import { getSettings } from './settings'

type Voice = 'place' | 'erase' | 'mistake' | 'win' | 'hint'

interface Tone {
  frequency: number
  duration: number
  gain: number
  type?: OscillatorType
}

const VOICES: Record<Voice, Tone[]> = {
  place: [{ frequency: 660, duration: 0.06, gain: 0.05 }],
  erase: [{ frequency: 320, duration: 0.05, gain: 0.04 }],
  // Ошибка звучит нисходящей парой: интонация «не то» узнаётся без обучения.
  mistake: [
    { frequency: 300, duration: 0.08, gain: 0.06, type: 'square' },
    { frequency: 200, duration: 0.12, gain: 0.06, type: 'square' },
  ],
  hint: [{ frequency: 880, duration: 0.07, gain: 0.05 }],
  win: [
    { frequency: 523, duration: 0.1, gain: 0.06 },
    { frequency: 659, duration: 0.1, gain: 0.06 },
    { frequency: 784, duration: 0.18, gain: 0.06 },
  ],
}

let context: AudioContext | null = null

function audio(): AudioContext | null {
  if (context) return context
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  context = new Ctor()
  return context
}

export function play(voice: Voice): void {
  if (!getSettings().sound) return

  const ctx = audio()
  if (!ctx) return
  // Браузер держит контекст приостановленным до первого жеста пользователя.
  if (ctx.state === 'suspended') void ctx.resume()

  let at = ctx.currentTime
  for (const tone of VOICES[voice]) {
    const oscillator = ctx.createOscillator()
    const envelope = ctx.createGain()

    oscillator.type = tone.type ?? 'sine'
    oscillator.frequency.value = tone.frequency
    // Мгновенный обрыв даёт щелчок, поэтому громкость гасится плавно.
    envelope.gain.setValueAtTime(tone.gain, at)
    envelope.gain.exponentialRampToValueAtTime(0.0001, at + tone.duration)

    oscillator.connect(envelope).connect(ctx.destination)
    oscillator.start(at)
    oscillator.stop(at + tone.duration)
    at += tone.duration
  }
}
